import {
  addDaysToKey,
  daysBetweenKeys,
  fmtDate,
  fmtTime,
  fmtVisitDay,
  siteDayKey,
  siteTodayKey,
  visitActualDateLabel,
  visitWhen,
} from '@/lib/dates';
import { subjectDisplayId } from '@/lib/subjectLabel';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Users,
  AlertTriangle,
  CalendarDays,
  ShieldAlert,
  ArrowUpRight,
  ClipboardList,
  FileCheck2,
  MoreHorizontal,
  Plus,
  ClipboardCheck,
  FileSignature,
  Loader2,
  ChevronRight,
  GraduationCap,
  MessageCircleQuestion,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  api,
  dashboard,
  studies,
  subjects,
  visits,
  compliance,
  signatureQueue,
  training,
  me as meApi,
  queryTypeFromOrigin,
  type MeResponse,
  type VisitRow,
  type ComplianceQueryRow,
  eisfDocumentHref,
} from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';
import { encryptId } from '@/lib/encryptId';
import { humanStatus, studyStatusBadgeClass } from '@/lib/humanise';
import { CAN_VIEW_QUERIES } from '@/lib/permissions';
import { useMyPermissions } from '@/hooks/useMyPermissions';
import { PageHeader } from '@/components/layout/PageHeader';


interface Health {
  status: string;
  service: string;
  stage: string;
  timestamp: string;
}

const TONE = {
  accent: 'bg-accent-50 text-accent-700',
  brand: 'bg-brand-50 text-brand-700',
  warning: 'bg-warning-100 text-warning-700',
  success: 'bg-success-100 text-success-700',
  danger: 'bg-danger-100 text-danger-700',
};

/**
 * Human-friendly date relative to today: Today / Tomorrow / Yesterday / the
 * date. Both days are read on the SITE's clock (23 Oct 2026): `new Date()`
 * on a zone-less Caspio value read it as the viewer's local time, so a
 * screening done the evening of 22 Oct in Los Angeles said "Today" on 23 Oct.
 */
function relativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const diff = daysBetweenKeys(siteTodayKey(), siteDayKey(iso));
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return fmtVisitDay(iso);
}

function visitLabel(v: VisitRow): string {
  return v.visit_label ?? v.visit_name ?? (v.unscheduled ? 'Unscheduled' : 'Visit');
}

function subjectLabel(v: VisitRow, subjectRefById: Map<string, string>): string {
  return subjectRefById.get(v.subject_id) ?? v.subject_id.slice(0, 8);
}

function visitStatusBadge(v: VisitRow): { label: string; cls: string } {
  if (v.status === 'completed') return { label: 'Completed', cls: 'badge-success' };
  if (v.status === 'missed') return { label: 'Missed', cls: 'badge-danger' };
  if (v.window_compliance === 'late') return { label: 'Overdue', cls: 'badge-danger' };
  if (v.window_compliance === 'early') return { label: 'Early', cls: 'badge-info' };
  if (v.unscheduled) return { label: 'Unscheduled', cls: 'badge-info' };
  return { label: 'Scheduled', cls: 'badge-info' };
}

function queryStatusBadge(s: ComplianceQueryRow['status']): { label: string; cls: string } {
  if (s === 'open') return { label: 'Open', cls: 'badge-warning' };
  if (s === 'answered') return { label: 'Answered', cls: 'badge-info' };
  return { label: 'Closed', cls: 'badge-neutral' };
}

function daysOpen(raisedAt: string, closedAt: string | null): number {
  const start = new Date(raisedAt).getTime();
  const end = closedAt ? new Date(closedAt).getTime() : Date.now();
  return Math.max(0, Math.round((end - start) / 86_400_000));
}

// ─── Study Approvals (Phase 2 Defect 2.3 — split from Documents-Awaiting) ─
// Site coordinators / PIs asked for study-onboarding approvals to sit in
// their own widget rather than being counted together with the eSignature
// queue (which mixes consent co-signs, visit signs, etc). Same query as
// before — just its own card. The queue lives in <SignatureQueueSection/>.
function StudyApprovalsSection({
  userId,
  activeSiteId,
  siteNames,
}: {
  userId: string;
  activeSiteId: string;
  siteNames: Record<string, string>;
}) {
  const studiesQ = useQuery({
    queryKey: ['studies', 'pi-pending', userId, activeSiteId],
    queryFn: async () => {
      const { rows } = await studies.list();
      return rows.filter(
        (r) =>
          (r.status === 'draft' || r.status === 'initiation') &&
          r.site_id === activeSiteId &&
          r.pi_user_id === userId,
      );
    },
  });

  // Combined feed: pending study approvals + signature-queue items +
  // standalone-training approvals, top 3. The PI/coordinator sees every
  // surface in one card so a hot queue item never buries a pending study
  // approval and vice versa.
  const queueQ = useQuery({
    queryKey: ['signature-queue', 'dashboard'],
    queryFn: () => signatureQueue.list({ limit: 5, page: 1 }),
    refetchInterval: 30_000,
  });

  // Standalone/external training — PI-approval-gated (client, 14 Aug
  // demo-review call). Same task-queue ApprovalNeededPage.tsx reads.
  const trainingTasksQ = useQuery({
    queryKey: ['me', 'tasks', 'open', 'dashboard'],
    queryFn: () => meApi.listTasks('open'),
    refetchInterval: 30_000,
  });

  const pendingStudies = studiesQ.data ?? [];
  const queueItems = queueQ.data?.items ?? [];
  const trainingTasks = (trainingTasksQ.data?.tasks ?? []).filter((t) => t.kind === 'training-approval-needed');
  const isLoading = studiesQ.isLoading || queueQ.isLoading || trainingTasksQ.isLoading;

  // Merge into one list sorted by most recent date, show only top 3.
  type StudyItem = { kind: 'study'; key: string; date: string; study: typeof pendingStudies[number] };
  type QueueItem = { kind: 'queue'; key: string; date: string; item: typeof queueItems[number] };
  type TrainingItem = { kind: 'training'; key: string; date: string; task: typeof trainingTasks[number] };
  const merged: (StudyItem | QueueItem | TrainingItem)[] = [
    ...pendingStudies.map((s): StudyItem => ({ kind: 'study', key: s.study_id, date: s.updated_at ?? s.created_at ?? '', study: s })),
    ...queueItems.map((it): QueueItem => ({ kind: 'queue', key: it.queue_item_id, date: it.generated_at, item: it })),
    ...trainingTasks.map((t): TrainingItem => ({ kind: 'training', key: t.task_id, date: t.created_at, task: t })),
  ].sort((a, b) => b.date.localeCompare(a.date));
  const displayed = merged.slice(0, 3);

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-surface-border px-6 py-4">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-amber-100 text-amber-700">
          <ClipboardCheck className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-ink-primary">Approvals</h2>
          <p className="text-xs text-ink-muted">Studies and trainings awaiting your sign-off.</p>
        </div>
        {!isLoading && merged.length > 0 && (
          <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-100 px-2 text-xs font-bold text-amber-700">
            {merged.length}
          </span>
        )}
        <Link
          to="/studies"
          className="ml-auto flex items-center gap-0.5 text-xs font-medium text-accent-700 hover:underline"
        >
          View All <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : merged.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-ink-muted">
          Nothing awaiting your approval.
        </div>
      ) : (
        <div className="divide-y divide-surface-border">
          {displayed.map((entry) => {
            if (entry.kind === 'study') {
              const study = entry.study;
              return (
                <Link
                  key={entry.key}
                  to={`/studies/${encryptId(study.study_id)}/onboarding`}
                  className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-muted/40"
                >
                  <ClipboardCheck className="h-4 w-4 shrink-0 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-ink-primary">{study.title}</div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      Study approval · {study.sponsor} · {siteNames[study.site_id] ?? 'Unknown site'}
                    </div>
                  </div>
                  <span className={studyStatusBadgeClass(study.status)}>{humanStatus(study.status)}</span>
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                </Link>
              );
            }
            if (entry.kind === 'training') {
              const task = entry.task;
              return (
                <Link
                  key={entry.key}
                  to="/approvals"
                  className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-muted/40"
                >
                  <ClipboardList className="h-4 w-4 shrink-0 text-accent-600" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-ink-primary">{task.message}</div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      Training approval · {fmtDate(task.created_at)}
                    </div>
                  </div>
                  <span className="rounded bg-accent-600 px-2.5 py-0.5 text-xs font-medium text-white">Review</span>
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                </Link>
              );
            }
            const it = entry.item;
            // Same tab, always: the session lives in sessionStorage, so a
            // target="_blank" link lands the new tab on /login. DoA and
            // training items open their signing dialog on the queue page
            // via ?open=<queue_item_id>.
            // eISF approvals go straight to the binder with the document open.
            const signHref = it.item_type === 'econsent'
              ? `/consent/staff-sign?cr=${encodeURIComponent(it.resource_id)}`
              : it.item_type === 'eisf_approval' && it.eisf
                ? eisfDocumentHref(it.eisf)
                : `/signature-queue?open=${encodeURIComponent(it.queue_item_id)}`;
            const kindLabel =
              it.item_type === 'econsent' ? 'Consent co-sign'
                : it.item_type === 'doa' || it.item_type === 'doa_countersign' ? 'Delegation sign'
                  : it.item_type === 'training' ? 'Training countersign'
                    : it.item_type === 'eisf_approval' ? 'eISF document approval'
                      : 'Visit sign';
            return (
              <Link
                key={entry.key}
                to={signHref}
                className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-muted/40"
              >
                <FileSignature className="h-4 w-4 shrink-0 text-accent-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink-primary">
                    {it.document_name}
                    {it.document_version ? ` · ${it.document_version}` : ''}
                  </div>
                  <div className="mt-0.5 text-xs text-ink-muted">
                    {kindLabel}
                    {(it.subject_ref ?? it.subject_id) ? ` · ${it.subject_ref ?? it.subject_id!.slice(0, 8)}` : ''}
                    {' · '}{fmtDate(it.generated_at)}
                  </div>
                </div>
                <span className={it.requires_review
                  ? 'rounded border border-surface-border px-2.5 py-0.5 text-xs font-medium text-ink-secondary'
                  : 'rounded bg-accent-600 px-2.5 py-0.5 text-xs font-medium text-white'}>
                  {it.requires_review ? 'Review' : 'Sign Now'}
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── My open queries ───────────────────────────────────────────────────
// Third card in the top row, alongside Approvals and Training. Shows the
// OPEN queries assigned to the signed-in user, oldest first — the one that
// has been waiting longest is the one to answer.
//
// GET /compliance/queries is guarded by a single requireOperation
// ('queries.read') with no requireStudyAccess behind it, so the caller is
// gated on exactly that operation at the call site and this never fires a
// request that comes back 403.
function MyQueriesSection() {
  const q = useQuery({
    queryKey: ['compliance-queries', 'mine', 'dashboard'],
    queryFn: () => compliance.listQueries({ assigned_to_me: true, status: 'open' }),
    refetchInterval: 30_000,
  });
  const open = (q.data?.rows ?? []).slice().sort((a, b) => a.raised_at.localeCompare(b.raised_at));
  const displayed = open.slice(0, 3);

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-surface-border px-6 py-4">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-violet-100 text-violet-700">
          <MessageCircleQuestion className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-ink-primary">Queries</h2>
          <p className="text-xs text-ink-muted">Open queries assigned to you.</p>
        </div>
        {!q.isLoading && open.length > 0 && (
          <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-violet-100 px-2 text-xs font-bold text-violet-700">
            {open.length}
          </span>
        )}
        <Link
          to="/queries"
          className="ml-auto flex items-center gap-0.5 text-xs font-medium text-accent-700 hover:underline"
        >
          View All <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {q.isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : open.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-ink-muted">
          No open queries assigned to you.
        </div>
      ) : (
        <div className="divide-y divide-surface-border">
          {displayed.map((row) => (
            <Link
              key={row.query_id}
              to="/queries"
              className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-muted/40"
            >
              <MessageCircleQuestion className="h-4 w-4 shrink-0 text-violet-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-ink-primary">{row.body}</div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {/* reference_number is the label people quote; query_id is a uuid. */}
                  {row.reference_number ?? 'Query'}
                  {row.raised_by_name ? ` · ${row.raised_by_name}` : ''}
                  {' · '}{fmtDate(row.raised_at)}
                </div>
              </div>
              <span className="rounded bg-violet-600 px-2.5 py-0.5 text-xs font-medium text-white">
                Answer
              </span>
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── My pending training ───────────────────────────────────────────────
// Client (UAT, Oct 2026): staff should see their outstanding trainings on
// the dashboard the same way approvers see pending approvals. Always the
// signed-in user's OWN assignments (GET /training/me), whatever their role —
// a PI who is also a trainee sees theirs here, not the whole site's.
// "Pending" = assigned but not yet signed by the trainee; anything already
// signed is waiting on the countersigner, not on them.
function MyTrainingSection() {
  const q = useQuery({
    queryKey: ['training', 'me', 'dashboard'],
    queryFn: () => training.listMine({ limit: 500 }),
    refetchInterval: 30_000,
  });
  const pending = (q.data?.rows ?? [])
    .filter((r) => r.status === 'assigned' || r.status === 'in_progress')
    // Oldest first — the longest-outstanding training is the one to chase.
    .sort((a, b) => a.assigned_at.localeCompare(b.assigned_at));
  const displayed = pending.slice(0, 3);
  const viewAllHref = '/training?status=assigned&mine=1';

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-surface-border px-6 py-4">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-accent-50 text-accent-700">
          <GraduationCap className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-ink-primary">My Training</h2>
          <p className="text-xs text-ink-muted">Trainings assigned to you that still need your sign-off.</p>
        </div>
        {!q.isLoading && pending.length > 0 && (
          <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-accent-50 px-2 text-xs font-bold text-accent-700">
            {pending.length}
          </span>
        )}
        <Link
          to={viewAllHref}
          className="ml-auto flex items-center gap-0.5 text-xs font-medium text-accent-700 hover:underline"
        >
          View All <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {q.isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : q.isError ? (
        <div className="flex items-center justify-center gap-2 px-6 py-8 text-sm text-danger-700">
          <AlertTriangle className="h-4 w-4" /> Could not load your trainings.
        </div>
      ) : pending.length === 0 ? (
        <div className="px-6 py-8 text-center text-sm text-ink-muted">
          No pending training. You're all caught up.
        </div>
      ) : (
        <div className="divide-y divide-surface-border">
          {displayed.map((row) => (
            <Link
              key={row.certification_id}
              to={viewAllHref}
              className="flex items-center gap-4 px-6 py-4 transition-colors hover:bg-surface-muted/40"
            >
              <GraduationCap className="h-4 w-4 shrink-0 text-accent-600" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-ink-primary">
                  {row.document_title || row.document_type_name}
                </div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {row.reference_number}
                  {row.study_protocol_number ? ` · ${row.study_protocol_number}` : ''}
                  {' · Assigned '}{fmtDate(row.assigned_at)}
                </div>
              </div>
              <span className="rounded bg-accent-600 px-2.5 py-0.5 text-xs font-medium text-white">Sign</span>
              <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Component ─────────────────────────────────────────────────────────
export function DashboardPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');

  const perms = useMyPermissions();
  const health = useQuery({ queryKey: ['health'], queryFn: () => api<Health>('/health') });
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<MeResponse>('/auth/me') });

  // ── Real data sources ───────────────────────────────────────────────
  // All scoped to the active session site via the BE's claims.site_id.
  // Use studies.portfolio() (site-scoped) — NOT studies.list() which is
  // delegation-scoped cross-site and causes a UX mismatch: Dashboard
  // tile counts studies at other sites, while /studies portfolio page
  // shows only current-site studies. Both surfaces MUST agree.
  const canSeeSubjects = perms.isVisible('subjects');
  const canSeeVisits = perms.isVisible('visits');
  const canSeeQueries = perms.isVisible('queries');
  const canSeeStudies = perms.isVisible('studies');
  const canSeeBinder = perms.isVisible('binder');
  const studiesQ = useQuery({
    queryKey: ['dashboard', 'studies', user?.activeSiteId ?? ''],
    queryFn: () => studies.portfolio({ lifecycle: 'all', pageSize: 100 }),
    enabled: canSeeStudies,
    staleTime: 30_000,
  });
  // The API refuses subjects/visits/queries for roles without clinical
  // access (platform admins, compliance). Firing them anyway produced a
  // console full of 403s on every dashboard load, and React Query retried
  // each one. Ask the same source the sidebar uses, so these follow the
  // RBAC grid once those modules are migrated.

  const enrolledSubjectsQ = useQuery({
    queryKey: ['dashboard', 'subjects', 'enrolled'],
    queryFn: () => subjects.list({ status: 'enrolled', limit: 1 }),
    enabled: canSeeSubjects,
    staleTime: 30_000,
  });
  const screeningSubjectsQ = useQuery({
    queryKey: ['dashboard', 'subjects', 'screening'],
    queryFn: () => subjects.list({ status: 'screening', limit: 1 }),
    enabled: canSeeSubjects,
    staleTime: 30_000,
  });
  // Visit rows only carry subject_id — fetch the site's subjects once to
  // resolve each visit's human-readable reference (e.g. SUB-0001) instead of
  // falling back to a truncated UUID.
  const allSubjectsQ = useQuery({
    queryKey: ['dashboard', 'subjects', 'all'],
    queryFn: () => subjects.list({ pageSize: 500 }),
    enabled: canSeeSubjects,
    staleTime: 30_000,
  });
  const subjectRefById = useMemo(() => {
    const m = new Map<string, string>();
    // Subject ID (IVRS) when issued, else "REF-SUB-xxxx" (client, 16 Oct 2026).
    (allSubjectsQ.data?.rows ?? []).forEach((s) => m.set(s.subject_id, subjectDisplayId(s)));
    return m;
  }, [allSubjectsQ.data]);
  const openQueriesQ = useQuery({
    queryKey: ['dashboard', 'queries', 'open'],
    queryFn: () => compliance.listQueries({ status: 'open' }),
    enabled: canSeeQueries,
    staleTime: 30_000,
  });
  // Today on the site's clock, not the UTC day.
  const todayKey = useMemo(() => siteTodayKey(), []);
  const upcomingVisitsQ = useQuery({
    queryKey: ['dashboard', 'visits', 'upcoming'],
    queryFn: () => visits.list({ scope: 'site', from: todayKey, to: addDaysToKey(todayKey, 30) }),
    enabled: canSeeVisits,
    staleTime: 30_000,
  });
  const pastVisitsQ = useQuery({
    queryKey: ['dashboard', 'visits', 'past'],
    queryFn: () => visits.list({ scope: 'site', from: addDaysToKey(todayKey, -30), to: todayKey }),
    enabled: canSeeVisits,
    staleTime: 30_000,
  });
  // Sprint-20 — eISF folder-tree AC11: "Studies Compliant" tile. Reflects
  // the count of studies whose binder has every Core folder filed with a
  // current-version document.
  const studiesCompliantQ = useQuery({
    queryKey: ['dashboard', 'studies-compliant'],
    queryFn: () => dashboard.studiesCompliant(),
    enabled: canSeeStudies,
    staleTime: 60_000,
  });

  // ── Derived dashboard data ──────────────────────────────────────────
  const activeStudiesCount = useMemo(
    () => (studiesQ.data?.rows ?? []).filter((s) => s.status === 'active').length,
    [studiesQ.data],
  );
  const enrolledCount = enrolledSubjectsQ.data?.count ?? 0;
  const screeningCount = screeningSubjectsQ.data?.count ?? 0;
  const openQueriesCount = openQueriesQ.data?.count ?? 0;
  const upcomingVisits = useMemo(
    () => (upcomingVisitsQ.data?.visits ?? []).slice().sort((a, b) => {
      const aDate = visitWhen(a) ?? '';
      const bDate = visitWhen(b) ?? '';
      return aDate.localeCompare(bDate);
    }),
    [upcomingVisitsQ.data],
  );
  const pastVisits = useMemo(
    () => (pastVisitsQ.data?.visits ?? []).slice().sort((a, b) => {
      const aDate = a.actual_date ?? a.completed_at ?? visitWhen(a) ?? '';
      const bDate = b.actual_date ?? b.completed_at ?? visitWhen(b) ?? '';
      return bDate.localeCompare(aDate); // most-recent first
    }),
    [pastVisitsQ.data],
  );
  const visitsThisWeekCount = useMemo(() => {
    // Compared day to day: the old check set a full timestamp against a
    // YYYY-MM-DD string, which dropped visits on the 7th day.
    const endKey = addDaysToKey(todayKey, 7);
    return upcomingVisits.filter((v) => {
      const d = visitWhen(v);
      return !!d && siteDayKey(d) <= endKey;
    }).length;
  }, [upcomingVisits, todayKey]);

  const showRecoveryWarning = !!me.data?.recovery_codes?.low;
  const remaining = me.data?.recovery_codes?.remaining ?? 0;
  const visitsToShow = tab === 'upcoming' ? upcomingVisits : pastVisits;
  const visitsLoading = (tab === 'upcoming' ? upcomingVisitsQ.isLoading : pastVisitsQ.isLoading);
  const openQueries = openQueriesQ.data?.rows ?? [];

  // Right-rail "next visit" — first upcoming, if any.
  const nextVisit = upcomingVisits[0];
  // Pinned card — Master Catalog admin shortcut for platform_admin only.
  const isPlatformAdmin =
    user?.role === 'platform_admin' || user?.role === 'application_admin';

  return (
    <div className="space-y-8">
      <PageHeader
        title="Overview"
        subtitle={`Welcome back, ${user?.fullName?.split(' ')[0] ?? 'Clinician'}. Here's what's on your plate today.`}
        actions={
          <>
            <button type="button" className="btn-secondary">
              <FileCheck2 className="h-4 w-4" /> Export weekly report
            </button>
            {user?.role !== 'monitor' && (
              <Link to="/subjects" className="btn-primary">
                <Plus className="h-4 w-4" /> New subject
              </Link>
            )}
          </>
        }
      />

      {/* Optional MFA recovery warning (real) */}
      {showRecoveryWarning && (
        <div className="flex items-start gap-3 rounded-xl border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-ink-primary">MFA recovery codes running low</div>
            <p className="mt-1 text-xs leading-relaxed">
              You have <span className="font-mono font-semibold">{remaining}</span> recovery code
              {remaining === 1 ? '' : 's'} left. Generate a fresh set before they run out.
            </p>
            <Link
              to="/auth/mfa/enrol?regenerate=1"
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-warning-700 underline"
            >
              Generate new recovery codes →
            </Link>
          </div>
        </div>
      )}

      {/* Phase 2 Defect 2.3 — split view. Study approvals and the eSignature
          queue used to share one "Documents Awaiting Your Review" card; site
          coordinators asked to see them side-by-side so a full queue doesn't
          bury a pending study approval (and vice versa). */}
      {user?.id && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <StudyApprovalsSection
            userId={user.id}
            activeSiteId={user.activeSiteId}
            siteNames={user.siteNames}
          />
          {/* holdsOperation, NOT isVisible. The card calls GET /training/me,
              which is guarded by training.read specifically — and isVisible
              is true when the role holds ANY training verb, so an approver
              who cannot read would have opened a card that 403s. Same union
              trap as the studies.create button. */}
          {perms.holdsOperation('training.read') && <MyTrainingSection />}
          {perms.holdsOperation('queries.read', CAN_VIEW_QUERIES) && <MyQueriesSection />}
        </div>
      )}

      {/* Stat tiles — live counts from the BE.
          auto-fit rather than a fixed 5 columns: tiles are removed when the
          role cannot open the module behind them, and a fixed track count
          would leave the row trailing off into empty columns. */}
      <section className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
        {[
          {
            label: 'Active studies',
            value: studiesQ.isLoading ? '—' : String(activeStudiesCount),
            delta: studiesQ.data ? `${studiesQ.data.rows.length} total` : ' ',
            icon: Activity,
            tone: 'accent' as const,
            to: '/studies',
            show: canSeeStudies,
          },
          {
            label: 'Studies compliant',
            // Sprint-20 — eISF folder-tree AC11. `X/Y compliant` where Y is
            // the number of studies at the active site with an instantiated
            // binder, X is the subset where every Core folder is filed.
            value: studiesCompliantQ.isLoading
              ? '—'
              : `${studiesCompliantQ.data?.compliant ?? 0}/${studiesCompliantQ.data?.total ?? 0}`,
            delta:
              !studiesCompliantQ.data || studiesCompliantQ.data.total === 0
                ? 'No binders instantiated'
                : studiesCompliantQ.data.compliant === studiesCompliantQ.data.total
                  ? 'All binders complete'
                  : `${studiesCompliantQ.data.total - studiesCompliantQ.data.compliant} missing required docs`,
            icon: FileCheck2,
            tone: 'success' as const,
            // The number is about studies, so it hides with the other study
            // tiles; the place you ACT on it is the binder, when visible.
            to: canSeeBinder ? '/binder' : '/studies',
            show: canSeeStudies,
          },
          {
            label: 'Subjects enrolled',
            value: enrolledSubjectsQ.isLoading ? '—' : String(enrolledCount),
            delta: screeningSubjectsQ.isLoading
              ? ' '
              : `${screeningCount} in screening`,
            icon: Users,
            tone: 'brand' as const,
            to: '/subjects',
            show: canSeeSubjects,
          },
          {
            label: 'Open queries',
            value: openQueriesQ.isLoading ? '—' : String(openQueriesCount),
            delta: openQueriesCount === 0 ? 'No outstanding queries' : 'Across all studies',
            icon: AlertTriangle,
            tone: 'warning' as const,
            to: '/queries',
            show: canSeeQueries,
          },
          {
            label: 'Visits next 7 days',
            value: upcomingVisitsQ.isLoading ? '—' : String(visitsThisWeekCount),
            delta: upcomingVisitsQ.data
              ? `${upcomingVisits.length} in next 30d`
              : ' ',
            icon: CalendarDays,
            tone: 'success' as const,
            to: '/visits',
            show: canSeeVisits,
          },
        ]
          // A tile whose module the role cannot open is removed, not greyed:
          // its number would be a fact about data they are not allowed to see,
          // and its link would land on a page PageRoute refuses.
          .filter((s) => s.show)
          .map((s) => (
            <Link key={s.label} to={s.to} className="card p-5 transition-colors hover:bg-surface-muted/40">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                    {s.label}
                  </div>
                  <div className="mt-2 text-[28px] font-semibold leading-none tracking-tight text-ink-primary">
                    {s.value}
                  </div>
                  <div className="mt-3 text-[11px] font-medium text-ink-secondary">
                    {s.delta}
                  </div>
                </div>
                <div className={cn('grid h-10 w-10 place-items-center rounded-xl', TONE[s.tone])}>
                  <s.icon className="h-5 w-5" />
                </div>
              </div>
            </Link>
          ))}
      </section>

      {/* Main 2-col layout */}
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        {/* ── Left column ───────────────────────────────── */}
        <div className="space-y-6">
          {/* Upcoming visits card */}
          <section className="card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
              <div>
                <h2 className="text-base font-semibold text-ink-primary">Visits</h2>
                <p className="text-xs text-ink-muted">Select a row to open the visit workspace.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-full bg-surface-muted p-1">
                  {(['upcoming', 'past'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTab(t)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold',
                        tab === t
                          ? 'bg-accent-600 text-white shadow-sm'
                          : 'text-ink-secondary hover:text-ink-primary',
                      )}
                    >
                      {t === 'upcoming' ? 'Upcoming' : 'Past'}
                      <span
                        className={cn(
                          'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                          tab === t ? 'bg-white/20 text-white' : 'bg-white text-ink-secondary',
                        )}
                      >
                        {t === 'upcoming' ? upcomingVisits.length : pastVisits.length}
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  aria-label="More"
                  className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:bg-surface-muted hover:text-ink-primary"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subject ID</th>
                    <th>Visit</th>
                    <th>Date</th>
                    <th>Actual date</th>
                    <th>Status</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visitsLoading ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-sm text-ink-muted">
                        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" /> Loading visits…
                      </td>
                    </tr>
                  ) : visitsToShow.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-10 text-center text-sm text-ink-muted">
                        No {tab === 'upcoming' ? 'upcoming' : 'past'} visits at this site.
                      </td>
                    </tr>
                  ) : (
                    visitsToShow.map((v) => {
                      const status = visitStatusBadge(v);
                      const actual = visitActualDateLabel(v);
                      const dateIso =
                        tab === 'upcoming'
                          ? visitWhen(v)
                          : v.actual_date ?? v.completed_at ?? visitWhen(v);
                      return (
                        <tr key={v.visit_id}>
                          <td>
                            <div className="font-mono text-[12px] font-semibold text-ink-primary">
                              {subjectLabel(v, subjectRefById)}
                            </div>
                          </td>
                          <td className="text-[12px]">{visitLabel(v)}</td>
                          <td className="text-[12px]">{relativeDate(dateIso)}</td>
                          <td className={`text-[12px] ${actual.recorded ? '' : 'text-ink-muted'}`}>
                            {actual.text}
                          </td>
                          <td>
                            <span className={status.cls}>{status.label}</span>
                          </td>
                          <td className="text-right">
                            <Link
                              to={`/visits/${encodeURIComponent(v.visit_id)}`}
                              className="inline-flex items-center gap-1 text-[12px] font-semibold text-accent-700 hover:text-accent-800"
                            >
                              Open →
                            </Link>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-surface-border bg-surface-subtle px-6 py-3 text-center">
              <Link to="/subjects" className="text-xs font-semibold text-accent-700 hover:text-accent-800">
                View all visits →
              </Link>
            </div>
          </section>

          {/* Active queries card */}
          <section className="card overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
              <div>
                <h2 className="text-base font-semibold text-ink-primary">Active queries</h2>
                <p className="text-xs text-ink-muted">Outstanding monitor / sponsor queries on your sites.</p>
              </div>
              <Link to="/queries" className="text-xs font-semibold text-accent-700 hover:text-accent-800">
                View all →
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Query type</th>
                    <th>Raised by</th>
                    <th>Topic</th>
                    <th>Days open</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openQueriesQ.isLoading ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-sm text-ink-muted">
                        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" /> Loading queries…
                      </td>
                    </tr>
                  ) : openQueries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-10 text-center text-sm text-ink-muted">
                        No open queries.
                      </td>
                    </tr>
                  ) : (
                    openQueries.map((q) => {
                      const s = queryStatusBadge(q.status);
                      const isExternal = queryTypeFromOrigin(q.origin) === 'external';
                      return (
                        <tr key={q.query_id}>
                          <td>
                            <span
                              className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                isExternal ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {isExternal ? 'External' : 'Internal'}
                            </span>
                          </td>
                          <td className="text-[12px] text-ink-primary">
                            {q.raised_by_name ?? q.raised_by_user_id.slice(0, 8)}
                          </td>
                          <td className="text-[12px] text-ink-primary">
                            <span className="line-clamp-1">{q.body}</span>
                          </td>
                          <td className="font-mono text-[12px]">{daysOpen(q.raised_at, q.closed_at)}d</td>
                          <td>
                            <span className={s.cls}>{s.label}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* ── Right rail (navy) ─────────────────────────── */}
        <aside className="space-y-6">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent-800 via-accent-900 to-[#162558] p-6 text-white shadow-rail">
            {/* Decorative dot pattern */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.12]"
              style={{
                backgroundImage:
                  'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1.2px)',
                backgroundSize: '14px 14px',
              }}
            />
            <div className="relative">
              <div className="flex items-center justify-between">
                <h3 className="text-[15px] font-semibold">Today at a glance</h3>
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/90">
                  Live
                </span>
              </div>
              <p className="mt-1 text-xs text-white/70">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>

              {/* Next visit — real next-upcoming from this site */}
              <div className="mt-5 rounded-xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70">
                  Next visit
                </div>
                {upcomingVisitsQ.isLoading ? (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-white/70">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </div>
                ) : nextVisit ? (
                  <>
                    <div className="mt-2 leading-tight">
                      <div className="text-[13px] font-semibold">{visitLabel(nextVisit)}</div>
                      <div className="text-[11px] text-white/70">Subject {subjectLabel(nextVisit, subjectRefById)}</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-2 text-white/80">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {relativeDate(visitWhen(nextVisit))}
                      </div>
                      <Link
                        to={`/visits/${encodeURIComponent(nextVisit.visit_id)}`}
                        className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-accent-700 hover:bg-white/90"
                      >
                        Open
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="mt-2 text-[11px] text-white/70">
                    No upcoming visits at this site.
                  </div>
                )}
              </div>

              {/* Open queries summary — real count, drives the same /queries page */}
              {openQueriesCount > 0 && (
                <div className="mt-3 rounded-xl bg-white/10 p-4 ring-1 ring-white/15">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/70">
                    Open queries
                  </div>
                  <div className="mt-2 flex items-start gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-warning-500/20 ring-1 ring-warning-500/40">
                      <AlertTriangle className="h-4 w-4 text-warning-100" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-[13px] font-semibold">
                        {openQueriesCount} open quer{openQueriesCount === 1 ? 'y' : 'ies'}
                      </div>
                      <div className="text-[11px] text-white/70">
                        Across studies at this site
                      </div>
                    </div>
                  </div>
                  <Link
                    to="/queries"
                    className="mt-3 block rounded-full border border-white/30 px-3 py-1.5 text-center text-[11px] font-semibold hover:bg-white/10"
                  >
                    Review now
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Pinned modules */}
          <div className="card p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[13px] font-semibold text-ink-primary">Pinned</h3>
              <button className="text-[11px] font-semibold text-accent-700 hover:text-accent-800">Edit</button>
            </div>
            <ul className="space-y-2">
              {[
                { label: 'eISF Binder', icon: ClipboardList, to: '/binder' },
                { label: 'Compliance Hub', icon: ShieldAlert, to: '/compliance' },
                ...(isPlatformAdmin
                  ? [{ label: 'eISF Master Catalog', icon: FileCheck2, to: '/admin/eisf-master-data' }]
                  : []),
              ].map((p) => (
                <li key={p.label}>
                  <Link
                    to={p.to}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 text-[12px] text-ink-secondary hover:bg-surface-subtle hover:text-ink-primary"
                  >
                    <span className="grid h-7 w-7 place-items-center rounded-lg bg-surface-muted text-ink-secondary">
                      <p.icon className="h-3.5 w-3.5" />
                    </span>
                    <span className="font-medium text-ink-primary">{p.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* System status (real backend ping, kept compact) */}
          <div className="card p-5">
            <h3 className="mb-2 text-[13px] font-semibold text-ink-primary">System status</h3>
            {health.isLoading && <div className="text-xs text-ink-muted">Checking…</div>}
            {health.isError && (
              <div className="flex items-start gap-2 text-xs text-danger-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Backend unreachable.</span>
              </div>
            )}
            {health.data && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-success-500" />
                  <span className="text-xs font-medium text-ink-primary capitalize">
                    {health.data.status}
                  </span>
                </div>
                <span className="text-[11px] text-ink-muted">
                  {health.data.stage} · {fmtTime(health.data.timestamp)}
                </span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
